import type { ISymbolInfo, ISymbolPrecisions } from "@aspen/model";
import CryptoJs from "crypto-js";
import { Dimensions, Platform } from "react-native";
import { CUSTOMER_ID, OPERATE_CUSTOMER_ID } from "../constant/index";
import type { IGenerateDataCacheKey } from "@adgm/cross-models";
import { ENV } from "@adgm/cross-config";
import dayjs from "dayjs";
import { i18nCrossUtil } from "./i18n";
import { userCountriesData } from "@aspen/assets/offlineData/usersCountries";

// 密码格式，数字+小写字母+大写字母+特殊符号，8位以上，不包含空格
export const passwordRegExp =
  /^(?!.*\s)(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]).{8,}$/;

// 校验password不包含password和test单词及其任意字母的大小写变体
export const validatePasswordExclusion = /^(?!.*\b(password|test)\b)/;

export const isClient = typeof window !== "undefined";

/**
 * @description: 校验密码格式是否正确
 * @params {
 *  password：密码，
 *  message：格式或者长度错误提示，
 * }
 * @return {Promise}
 */
export const passwordValidator: (password: string, message: string) => Promise<any> = (
  // any 不修改，promise不强制数据结构
  password,
  message,
) => {
  const lowerCasePassword = password.toLowerCase();
  if (
    !password ||
    !validatePasswordExclusion.test(lowerCasePassword) ||
    !passwordRegExp.test(password)
  ) {
    return Promise.reject(new Error(message));
  }
  return Promise.resolve();
};

/**
 * @description: 校验确认密码格式以及是否和密码一致，重置密码和设置密码使用
 * @params {
 *  confirmPassword：确认密码，
 *  password：密码，
 *  message：格式或者长度错误提示，
 *  notMatch：两次密码不一致时的提示
 * }
 * @return {Promise}
 */
export const passwordConfirmValidator: (
  confirmPassword: string,
  password: string,
  message: string,
  notMatch: string,
) => Promise<any> = (
  // 不修改，promise不强制数据结构
  confirmPassword,
  password,
  message,
  notMatch,
) => {
  if (!confirmPassword) {
    return Promise.reject(new Error(message));
  }
  if (password !== confirmPassword) {
    return Promise.reject(new Error(notMatch));
  }
  const lowerCaseConfirmPassword = confirmPassword.toLowerCase();
  // 校验password不包含password和test单词及其任意字母的大小写变体
  const regex = /^(?!.*\b(password|test)\b)/;
  if (
    !confirmPassword ||
    password.length !== confirmPassword.length ||
    !regex.test(lowerCaseConfirmPassword) ||
    !passwordRegExp.test(confirmPassword)
  ) {
    return Promise.reject(new Error(message));
  }
  return Promise.resolve();
};

/**
 * @description: 校验密码格式是否正确
 * @params {
 *  password：密码
 * }
 * @return {boolean}
 */
export const checkPassword: (password: string) => boolean = (password: string) => {
  const lowerCasePassword = password.toLowerCase();
  return (
    Boolean(password) &&
    passwordRegExp.test(password) &&
    validatePasswordExclusion.test(lowerCasePassword)
  );
};

/**
 * @description: 获取交易对盘口的价格、涨跌幅和价格, 若无法直接从marketSymbolsList获取，尝试根据稳定币(USDC)，计算目标交易对数据
 * @params {
 *  targetSymbol: string - 目标交易对，例如 "BTC_USDC"（marketSymbolsList已有数据）或 "ETH_BTC"（需根据 BTC_USDC 和 ETH_USDC 计算）
 *  marketSymbolsList: ISymbolInfo - 市场交易对价格列表，例如 { BTC_USDC: IMarketPushItemType, ETH_USDC: IMarketPushItemType }
 *  activeSymbolPrecisions: ISymbolPrecisions - 精度信息，例如 { BTC_USDC: { symbolPrecision: 2 }, ETH_USDC: { symbolPrecision: 3 } }
 * }
 * @returns { { price: number | null, riseAndFall: number | null, pricePrecision: number | null } } - 返回目标交易对的原始价格、涨跌幅，组件渲染时自行处理精度
 * 如果无法计算，则返回默认值 { price: null, riseAndFall: null, pricePrecision: null }
 */
export const getPriceAndRiseBySymbol = (
  targetSymbol: string,
  marketSymbolsList: ISymbolInfo,
  activeSymbolPrecisions: ISymbolPrecisions,
): { price: number | null; riseAndFall: number | null; pricePrecision: number | null } => {
  const [baseCurrency, quoteCurrency] = targetSymbol.split("_"); // 分割目标交易对，例如 "ETH_BTC" -> ["ETH", "BTC"]

  // 检查目标交易对是否存在于 marketSymbolsList，如果存在则直接返回
  const targetMarketData = marketSymbolsList[targetSymbol];
  if (targetMarketData) {
    const pricePrecision = activeSymbolPrecisions[targetSymbol]?.symbolPrecision ?? null;
    return {
      price: targetMarketData.priceLast ?? null,
      riseAndFall: targetMarketData.riseAndFall ?? null,
      pricePrecision,
    };
  }

  // 检查是否可以通过其他交易对计算价格
  const stablecoin = "USDC"; // 默认稳定币
  const defaultPricePrecision = 6; // 默认价格精度 如ETH_BTC

  const baseToStablecoinMarketData = marketSymbolsList[`${baseCurrency}_${stablecoin}`];
  const quoteToStablecoinMarketData = marketSymbolsList[`${quoteCurrency}_${stablecoin}`];

  if (baseToStablecoinMarketData && quoteToStablecoinMarketData) {
    // base 对稳定币的价格，涨跌幅
    const { priceLast: baseToStablecoinPrice, riseAndFall: baseToStablecoinRiseAndFall } =
      baseToStablecoinMarketData;
    // quote 对稳定币的价格，涨跌幅
    const { priceLast: quoteToStablecoinPrice, riseAndFall: quoteToStablecoinRiseAndFall } =
      quoteToStablecoinMarketData;

    // quoteToStablecoinPrice 若<=0，按异常处理
    // quoteToStablecoinRiseAndFall 可能为负数，表示价格下跌，若<=-100，按异常处理
    if (quoteToStablecoinPrice <= 0 || quoteToStablecoinRiseAndFall <= -100) {
      return { price: null, riseAndFall: null, pricePrecision: null };
    }
    const calculatedRiseAndFall =
      ((baseToStablecoinRiseAndFall - quoteToStablecoinRiseAndFall) /
        (100 + quoteToStablecoinRiseAndFall)) *
      100;

    return {
      price: baseToStablecoinPrice / quoteToStablecoinPrice, // 计算目标交易对的价格
      riseAndFall: calculatedRiseAndFall, // 目标交易对涨跌幅
      pricePrecision: defaultPricePrecision,
    };
  }

  // 如果无法计算，返回默认值
  return { price: null, riseAndFall: null, pricePrecision: null };
};
export const safeJsonParse = <T = unknown>(jsonString: string): T | undefined => {
  try {
    return JSON.parse(jsonString);
  } catch (err) {
    return undefined;
  }
};

export const IS_WEB = Platform.OS === "web";
export const MODAL_NEED_HIDE = "MODAL_NEED_HIDE";
/**
 * 供 app 端使用的字符串全量替换工具函数。
 * 注意：web 端请优先使用原生 String.prototype.replaceAll。
 */
export const replaceAll = (text: string, searchValue: string, replaceValue: string): string => {
  return text.split(searchValue).join(replaceValue);
};

export const aesEncrypt = (key: string, text: string): string | undefined => {
  try {
    return CryptoJs.AES.encrypt(text, key).toString();
  } catch (e) {
    return undefined;
  }
};

export const aesDecrypt = (key: string, text: string): string | undefined => {
  try {
    return CryptoJs.AES.decrypt(text, key).toString(CryptoJs.enc.Utf8);
  } catch (e) {
    return undefined;
  }
};

/**
 * @description: 生成key
 * @param param 生成key的参数，eg: type=0&offset=0&limit=30
 * 约定： 相同接口，相同参数，那么不管在任何页面，初始数据是可以共用的。
 * customerId 一般不需要传递，帮客户时注意测试，也可以自行传递
 * @return string
 */
export const generateDataCacheKey: (param: IGenerateDataCacheKey) => string = ({
  params,
  api,
  language,
  customerId,
}) => {
  const _language = language;
  let _localCustomerId = "";
  if (!IS_WEB) {
    _localCustomerId = global.customerId;
  } else {
    _localCustomerId =
      (isClient &&
        (localStorage.getItem(OPERATE_CUSTOMER_ID) || sessionStorage.getItem(CUSTOMER_ID))) ||
      "";
  }
  // OPERATE_CUSTOMER_ID 保存的帮客或代客的客户Id，如果是帮客或代客 需要查询的是被操作客户缓存的值(web only)
  const _customerId = customerId ? customerId : _localCustomerId;
  const _env = isClient && ENV;
  let _key = "";
  if (_language) {
    _key = `${_env}_cache_${_language}_${_customerId}_${api}_${params}`;
  }
  _key = `${_env}_cache_${_customerId}_${api}_${params}`;
  return replaceAll(_key, "_", "-");
};

/**
 * 按照精度修复用户输入的金额，用于输入框金额输入
 * @param numOrString
 * @param digital
 * @returns {string}
 */
export const fixNumString: (numOrString: string | number, digital: number) => string = (
  numOrString,
  digital,
) => {
  numOrString = numOrString ?? "";
  const numStr = String(numOrString);
  if (numOrString === ".") {
    return "0.";
  }
  if (numStr.indexOf(".") > 0) {
    // 位数大于0
    if (digital > 0) {
      const re = RegExp(`^(\\d+)\\.(\\d{1,${digital}}).*$`, "gim");
      const str = numStr.replace(re, "$1.$2");
      const strArr = str.split(".");
      let ret = String(Number(strArr[0]));
      if (strArr[0].indexOf("-") > -1 && Number(strArr[0]) === 0) {
        ret = `-${ret}`;
      }
      if (strArr.length === 2) {
        ret = `${ret}.${strArr[1]}`; // 单纯输入10. 的时候， 补0
      }
      return ret;
    }
    // 只能输入整数时
    // 单纯输入"10." 的时候去掉小数点
    return numStr.substr(0, numStr.indexOf("."));
  }
  return numStr;
};

/**
 * @description: 处理数字，小数点后最多X位, 默认是8, 截取而不是四舍五入
 * @params {
 * value：需要处理的数字（或者数字字符串）
 * }
 * @return {string}
 */
// @ts-ignore
export const decimalPointNoMoreX = (value, x = 8) => {
  if (isNaN(Number(value))) return value;
  if (typeof value === "number" || typeof value === "string") {
    const str = `${value}`;
    if (/e/i.test(str) && typeof value === "number") {
      value = value.toFixed(18).replace(/\.?0+$/, ""); //转换科学技术法
    }
    value = value.toString();
    const pos = value.indexOf(".");
    // 判断是否有小数点
    if (pos > -1) {
      if (x == 0) return value.split(".")[0];
      value.substring(pos + 1).length > x ? (value = value.substring(0, pos + x + 1)) : null;
      const valueDicimalLength = value.split(".")[1].length;
      if (valueDicimalLength < x) {
        value = value + "0".repeat(x - valueDicimalLength);
      }
    } else if (x > 0) {
      value = `${value}.${"0".repeat(x)}`;
    }
    return value;
  }
};

/**
 * 数字千分位化
 * 每隔三位加逗号
 * @param number 要处理的数字
 * @returns {string}
 */
export const numberToThousands: (number: number | string) => number | string = (
  number,
): number | string => {
  if (!number) {
    return number;
  }
  number = scientificToNumber(number).toString();
  const list = number.split(".");
  const prefix = list[0].startsWith("-") ? "-" : "";
  let num = prefix ? list[0].slice(1) : list[0];
  let result = "";
  while (num.length > 3) {
    result = `,${num.slice(-3)}${result}`;
    num = num.slice(0, num.length - 3);
  }
  if (num) {
    result = num + result;
  }
  return `${prefix}${result}${list[1] ? `.${list[1]}` : ""}`;
};

/**
 * @description: 转化科学计数法
 * @params value 需要处理的数字/字符串
 * @return string 处理后的数值
 */
export const scientificToNumber: (value: number | string) => number | string = value => {
  if (isNaN(Number(value)) || !/e/i.test(value.toString())) {
    return value;
  }
  return Number(value)
    .toFixed(18)
    .replace(/\.?0+$/, "");
};

/**
 * 通用像素值转换函数
 *
 * 将UI给的像素值(切图上的px值)，转化为当前设备需要的数值
 * elemSize * (layoutWidthSize / designWidthSize )
 * @param n
 * @returns {number}
 */
export const tranformPx = (n: number, maxScale = 1.2): number => {
  if (IS_WEB) return n;
  const WIDTH = Dimensions.get("window").width;
  let scale = WIDTH / 375;
  scale = scale > maxScale ? maxScale : scale;
  const displayPX = Math.round(n * scale);
  if (Platform.OS === "android" && displayPX < 1) {
    return 1;
  }
  return displayPX;
};

/**
 * 生成 Table 筛选下拉 options，支持多语言和首字母大写。
 * @param optionsMap 选项映射，key 为选项值，value 为多语言 key
 * @returns options 数组，结构为 [{ label, value }]
 * @example
 * optionsMap = { limit: "order.type.limit", market: "order.type.market" }
 * 返回：[{ label: "ALL", value: "" }, { label: "Limit", value: "limit" }, { label: "Market", value: "market" }]
 */
export const formatTableSelectOptions = (optionsMap: Record<string, string>) => {
  const intl = i18nCrossUtil.t();
  if (
    !optionsMap ||
    typeof optionsMap !== "object" ||
    Array.isArray(optionsMap) ||
    !Object.keys(optionsMap).length
  ) {
    return [{ label: intl["table.filter.all"], value: "" }];
  }
  const keys = Object.keys(optionsMap);
  const options = [
    { label: intl["table.filter.all"], value: "" },
    ...keys
      .filter(key => typeof key === "string" && typeof optionsMap[key] === "string")
      .map(key => ({
        // 首字母大写处理：将多语言内容按单词分割，每个单词首字母大写，其余小写
        label: intl[optionsMap[key]]?.replace(
          /\b\w+/g,
          w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
        ),
        value: key,
      })),
  ];
  // 根据 label 字段去重
  const uniqueOptions = options.filter(
    (item, index, arr) => arr.findIndex(opt => opt.label === item.label) === index,
  );
  return uniqueOptions;
};

/**
 * 将日期区间['YYYY-MM-DD', 'YYYY-MM-DD']转为时间戳区间
 * @param dateRange 日期区间 [start, end]，格式为 'YYYY-MM-DD'
 * @returns { startTimestamp: number, endTimestamp: number } | null
 *   - 正常返回对象：{ startTimestamp, endTimestamp }，单位为毫秒时间戳
 *   - 输入无效时返回 null
 * @example
 *   // 2024-07-08 00:00:00 ~ 2025-07-07 23:59:59
 *   parseDateRangeToTimestamps(["2024-07-08", "2025-07-07"])
 */
export function parseDateRangeToTimestamps(dateRange: [string, string] | null): {
  startTimestamp: number;
  endTimestamp: number;
} | null {
  if (!Array.isArray(dateRange) || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
    return null;
  }
  const startTimestamp = dayjs(dateRange[0]).startOf("day").valueOf();
  const endTimestamp = dayjs(dateRange[1]).endOf("day").valueOf();
  return { startTimestamp, endTimestamp };
}

export const padZeroIfLessThanTen = (num: number) => {
  if (num < 10) {
    return "0" + num;
  }
  return String(num);
};

/**
 * 获取国家显示名称
 * 兼容性处理：country是string类型，先尝试JSON.parse，如果失败则从userCountriesData查找
 * @param country - 国家字段值，可能是JSON字符串或普通字符串
 * @returns 国家显示名称
 */
export const getCountryDisplayName = (country?: string): string => {
  if (!country) return "--";

  try {
    // 优先尝试JSON.parse获取commonName
    const countryData = JSON.parse(country);
    return countryData?.commonName || "--";
  } catch (jsonError) {
    // JSON解析失败，说明是普通string，从userCountriesData查找
    try {
      const countryInfo = userCountriesData?.find(countryItem => countryItem.id === country);
      return countryInfo?.commonName || country;
    } catch (error) {
      // 如果所有处理都失败，直接使用country值
      return country;
    }
  }
};
